import { Box, Collapse, Flex, Grid, useBreakpointValue } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaHeading, RanaTable } from "@nens/ranalib";
import { RanaLoading } from "@nens/ranalib";
import { useDebounce } from "@nens/react/hooks";
import { useLastTruthyValue } from "@nens/react/hooks";

import { BackButton } from "@/components/back-button";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useQuerySearchState } from "@/hooks/use-query-search-state";
import { useSetCatalogueSectionChildState } from "@/lib/catalogue/components/catalogue-section";
import { useDatasetListQuery, useGetDatasetQuery } from "@/queries/query-dataset";

import { DatasetDetail } from "../detail/dataset-detail";

import { DatasetCategoryCard } from "./components/dataset-category-card";
import { DatasetList } from "./components/dataset-list";

export const DATASET_SEARCH_KEY = "dataset";
export const CATEGORY_SEARCH_KEY = "category";

const TRANSITION_TIME = 200;
const DEFAULT_CATEGORY_QUERY_PARAMS = {
  limit: 0,
  search: undefined,
  facetSize: 9999,
};

export type GoToExtractDatasetProcessFunc = (
  params: { datasetId: string; projectSlug: string } | { jobId: string; projectSlug: string }
) => void;

const getBackButtonInfo = (
  selectedCategory: string | undefined,
  searchString: string,
  overrideOpenCategoryPicker: boolean
): { isForward: boolean; label: React.ReactNode } => {
  if (selectedCategory) {
    return {
      isForward: false,
      label: (
        <FormattedMessage id="button.back_to_categories" defaultMessage="Back to categories" />
      ),
    };
  }

  if (searchString && overrideOpenCategoryPicker) {
    return {
      isForward: true,
      label: <FormattedMessage id="button.to_results" defaultMessage="To results" />,
    };
  }

  return {
    isForward: false,
    label: <FormattedMessage id="button.choose_category" defaultMessage="Choose category" />,
  };
};

type DatasetOverviewProps = {
  goToExtractDatasetProcess: GoToExtractDatasetProcessFunc;
  // Fixed category and spatialrepresentationtype are used when the
  // Catalogue is used as a selection component for instance in Process input forms.
  fixedCategory?: string;
  fixedSpatialRepresentationType?: string;
  disableSettingSearchParams?: boolean;
  isSectionExpanded?: boolean;
};

export const DatasetOverview = ({
  goToExtractDatasetProcess,
  fixedCategory,
  fixedSpatialRepresentationType,
  disableSettingSearchParams,
  isSectionExpanded = true,
}: DatasetOverviewProps) => {
  const intl = useIntl();

  const [overrideOpenCategoryPicker, setOverrideOpenCategoryPicker] = useState(false);
  const [selectedCategory, _setSelectedCategory] = useRanaSearchState<string | undefined>(
    CATEGORY_SEARCH_KEY,
    fixedCategory,
    ["string", "undefined"],
    {
      disableSettingParams: disableSettingSearchParams,
    }
  );

  const setSelectedCategory = (selectedCategory: string | undefined) => {
    _setSelectedCategory(selectedCategory);
    setOverrideOpenCategoryPicker(false);
  };

  const {
    searchString,
    setSearchString,
    page,
    offset,
    limit,
    setPageSize,
    setPage,
    setSearchField,
  } = useRanaTableData({
    initialOrderings: { lastActivity: "za" },
    initialPage: 1,
    keyAddon: "catalogue",
  });

  const debouncedSearchString = useDebounce(searchString, 500);

  const categoryQuery = useDatasetListQuery(DEFAULT_CATEGORY_QUERY_PARAMS);
  const categoryWithSearchQuery = useDatasetListQuery({
    ...DEFAULT_CATEGORY_QUERY_PARAMS,
    search: debouncedSearchString && overrideOpenCategoryPicker ? debouncedSearchString : undefined,
  });
  const categoryObjectWithSearch = useLastTruthyValue(
    categoryWithSearchQuery.data?.facets?.category
  );

  const categoryObject = categoryQuery.data?.facets?.category;
  const categories = Object.keys(categoryObject ?? {}).sort();

  const itemsInFirstRow = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 }) ?? 1;
  const visibleCategories = isSectionExpanded ? categories : categories.slice(0, itemsInFirstRow);

  const openCategoryPicker = (!selectedCategory && !searchString) || overrideOpenCategoryPicker;

  const getDatasetQuery = useGetDatasetQuery();
  const {
    data: dataset,
    setData: setDataset,
    isLoading: datasetLoading,
  } = useQuerySearchState(DATASET_SEARCH_KEY, getDatasetQuery, {
    disableSettingParams: disableSettingSearchParams,
  });

  const isLoading = categoryQuery.isLoading || datasetLoading;
  const hasBackButton =
    !!dataset || ((!openCategoryPicker || !!searchString) && fixedCategory === undefined);
  useSetCatalogueSectionChildState({
    hasBackButton,
    hasStickyElement: false,
    hasExpandedState: true,
  });

  if (isLoading) return <RanaLoading h="full" isCentered />;

  if (dataset)
    return (
      <Box>
        <BackButton back={() => setDataset(undefined)}>
          <FormattedMessage id="button.back_to_datasets" defaultMessage="Back to datasets" />
        </BackButton>
        <DatasetDetail goToExtractDatasetProcess={goToExtractDatasetProcess} dataset={dataset} />
      </Box>
    );

  const backButtonInfo = getBackButtonInfo(
    selectedCategory,
    searchString,
    overrideOpenCategoryPicker
  );
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      <Flex flexGrow={1} position="relative" flexDirection="column">
        <Flex
          pointerEvents="none"
          sx={{ "*": { pointerEvents: "all" } }}
          position="relative"
          zIndex={3}
          flexWrap="wrap"
          pb={4}
          gap={2}
          justifyContent="space-between"
        >
          <Flex flexDir="column" justifyContent="center">
            <Collapse in={hasBackButton} unmountOnExit>
              <BackButton
                back={() => {
                  setSelectedCategory(undefined);
                  if (searchString) {
                    setOverrideOpenCategoryPicker(!overrideOpenCategoryPicker);
                  }
                }}
                isForward={backButtonInfo.isForward}
              >
                {backButtonInfo.label}
              </BackButton>
            </Collapse>

            <RanaHeading type="h2">
              {selectedCategory ?? (
                <FormattedMessage id="heading.categories" defaultMessage="Categories" />
              )}
            </RanaHeading>
          </Flex>

          <RanaTable.FilterComponents.Search
            placeholder={intl.formatMessage({
              id: "form.search",
              defaultMessage: "Search",
            })}
            searchString={searchString}
            setSearchString={(searchString) => {
              setOverrideOpenCategoryPicker(false);
              setSearchString(searchString);
            }}
          />
        </Flex>

        <Flex flexGrow={1} h="fit" position="relative">
          <Box
            h="full"
            w="full"
            alignItems="center"
            justifyContent="center"
            zIndex={2}
            opacity={openCategoryPicker ? 1 : 0}
            pointerEvents={openCategoryPicker ? "all" : "none"}
            transition={`all ${TRANSITION_TIME}ms`}
          >
            <Grid templateColumns={`repeat(${itemsInFirstRow}, 1fr)`} gap={4}>
              {visibleCategories.map((category) => (
                <DatasetCategoryCard
                  key={category}
                  setSelectedCategory={setSelectedCategory}
                  category={category}
                  amountInCategory={(categoryObjectWithSearch ?? categoryObject)?.[category] ?? 0}
                />
              ))}
            </Grid>
          </Box>

          <Flex
            position="absolute"
            top={0}
            left={0}
            w="full"
            h="full"
            flexGrow={1}
            opacity={openCategoryPicker ? 0 : 1}
            transition={`all ${TRANSITION_TIME}ms`}
          >
            <DatasetList
              category={selectedCategory}
              spatialRepresentationType={fixedSpatialRepresentationType}
              setDataset={setDataset}
              {...{ searchString, page, offset, limit, setPageSize, setPage, setSearchField }}
            />
          </Flex>
        </Flex>
      </Flex>
    </motion.div>
  );
};
